// ===== 1. Imports =====
import React, { useState } from "react";
import Modal from "../../components/Modal";
import { db, storage } from "../../firebaseConfig";
import {
  addDoc,
  collection,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Eye, Maximize2, Minimize2, Upload } from "lucide-react";
import { fmt } from "./utils/formatters";

// ===== 2. Componente =====
export default function InvoicePanel({
  projectCode,
  pos = [],
  flatInvoices = [],
  userRole,
  userCanCompleteInvoice,
  onEditInvoice,
  onFullscreen,
  isFullscreen = false,
}) {
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    poId: "",
    poNumber: "",
    supplier: "",
    description: "",
    reportedTotal: "",
    dueDate: "",
    file: null,
  });

  // ===== 3. Subir nueva factura =====
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!projectCode) return;

    if (!form.poId || !form.poNumber)
      return alert("Selecciona la PO correctamente");
    if (!form.file) return alert("Sube un archivo de factura");
    if (!form.description.trim())
      return alert("Añade una descripción de la factura");
    if (!form.reportedTotal)
      return alert("Introduce el importe total de la factura");

    try {
      setUploading(true);

      const invRef = await addDoc(
        collection(db, "projects", projectCode, "pos", form.poId, "invoices"),
        {
          poId: form.poId,
          poNumber: form.poNumber,
          supplier: form.supplier || "",
          description: form.description.trim(),
          reportedTotal: parseFloat(form.reportedTotal),
          dueDate: form.dueDate || "",
          fileName: form.file.name,
          mimeType: form.file.type,
          createdAt: serverTimestamp(),
          createdBy: userRole || "Usuario",
          invoiceNumber: "",
          baseAmount: 0,
          taxPercent: 21,
          totalAmount: 0,
          coded: false,
          canceled: false,
        }
      );

      const path = `projects/${projectCode}/pos/${form.poId}/invoices/${invRef.id}/${form.file.name}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, form.file);
      const url = await getDownloadURL(storageRef);

      await updateDoc(invRef, { fileUrl: url, storagePath: path });

      alert("Factura subida correctamente");
      setShowUpload(false);
      setForm({
        poId: "",
        poNumber: "",
        supplier: "",
        description: "",
        reportedTotal: "",
        dueDate: "",
        file: null,
      });
    } catch (err) {
      console.error("Error subiendo factura:", err);
      alert("Error al subir la factura. Revisa la consola.");
    } finally {
      setUploading(false);
    }
  };

  // ===== 4. Render =====
  return (
    <div className="card" style={{ padding: 20 }}>
      {/* --- Cabecera --- */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <h2 style={{ margin: 0 }}>Panel facturas</h2>
        <div style={{ display: "flex", gap: 8 }}>
          {/* Botón Subir factura */}
          <button
            className="button button--primary"
            title="Subir factura"
            onClick={() => setShowUpload(true)}
            style={{
              height: 36,
              padding: "0 14px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Upload size={18} />
          </button>

          {/* Botón Pantalla completa */}
          <button
            className="button"
            onClick={onFullscreen}
            title={
              isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"
            }
            style={{
              height: 36,
              padding: "0 14px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
        </div>
      </div>

      {/* --- Tabla de facturas --- */}
      <table className="users-table" style={{ marginTop: 8 }}>
        <thead>
          <tr>
            {isFullscreen ? (
              <>
                <th>ID</th>
                <th>N.º factura</th>
                <th>PO</th>
                <th>Proveedor</th>
                <th>Descripción</th>
                <th>Importe</th>
                <th>Total</th>
                <th>Vencimiento</th>
                <th></th>
              </>
            ) : (
              <>
                <th>ID</th>
                <th>PO</th>
                <th>Proveedor</th>
                <th>Importe</th>
                <th></th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {flatInvoices.length === 0 && (
            <tr>
              <td
                colSpan={isFullscreen ? 9 : 5}
                style={{ textAlign: "center", color: "var(--muted)" }}
              >
                No hay facturas registradas.
              </td>
            </tr>
          )}

          {flatInvoices.map((inv) => (
            <tr key={inv.id}>
              <td>{inv.id}</td>
              {isFullscreen && <td>{inv.invoiceNumber || "—"}</td>}
              <td>{inv.poNumber}</td>
              <td>{inv.supplier || "—"}</td>
              {isFullscreen && (
                <td className="truncate">{inv.description || "—"}</td>
              )}
              <td style={{ textAlign: "right" }}>
                {fmt(inv.reportedTotal || inv.baseAmount || 0)}
              </td>
              {isFullscreen && (
                <>
                  <td style={{ textAlign: "right" }}>
                    {fmt(inv.totalAmount || inv.reportedTotal || 0)}
                  </td>
                  <td>
                    {inv.dueDate
                      ? new Date(inv.dueDate).toLocaleDateString("es-ES")
                      : "—"}
                  </td>
                </>
              )}
              <td style={{ textAlign: "right" }}>
                <button
                  className="button button--ghost"
                  onClick={() => onEditInvoice(inv)}
                >
                  <Eye size={16} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* --- Modal: Subir factura --- */}
      {showUpload && (
        <Modal
          title="Subir factura"
          onClose={() => setShowUpload(false)}
          style={{ maxWidth: 700 }}
        >
          <form className="stack" onSubmit={handleUpload}>
            <div className="form-grid">
              {/* PO vinculada */}
              <div className="field">
                <label>PO vinculada</label>
                <select
                  value={form.poId}
                  onChange={(e) => {
                    const po = pos.find((x) => x.id === e.target.value);
                    setForm((f) => ({
                      ...f,
                      poId: e.target.value,
                      poNumber: po?.number || "",
                      supplier: po?.supplier || "",
                    }));
                  }}
                  required
                >
                  <option value="">Selecciona…</option>
                  {pos.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.number} · {p.supplier}
                    </option>
                  ))}
                </select>
              </div>

              {/* Proveedor */}
              <div className="field">
                <label>Proveedor</label>
                <input
                  type="text"
                  value={form.supplier}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, supplier: e.target.value }))
                  }
                  disabled={!!form.poId}
                  placeholder="Nombre del proveedor"
                  required={!form.poId}
                />
              </div>

              {/* Descripción */}
              <div className="field field--span2">
                <label>Descripción</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, description: e.target.value }))
                  }
                  placeholder="Ej. Generador, alquiler material, transporte..."
                  required
                />
              </div>

              {/* Importe total reportado */}
              <div className="field">
                <label>Importe total (con IVA)</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.reportedTotal}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, reportedTotal: e.target.value }))
                  }
                  placeholder="0.00"
                  required
                />
              </div>

              {/* Vencimiento */}
              <div className="field">
                <label>Vencimiento</label>
                <input
                  type="date"
                  value={form.dueDate}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, dueDate: e.target.value }))
                  }
                />
              </div>

              {/* Archivo */}
              <div className="field field--span2">
                <label>Archivo factura (PDF, JPG, PNG)</label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) setForm((f) => ({ ...f, file }));
                  }}
                  required
                />
                {form.file && (
                  <p style={{ marginTop: 6, fontSize: 13 }}>
                    Archivo: <strong>{form.file.name}</strong> (
                    {Math.round(form.file.size / 1024)} KB)
                  </p>
                )}
              </div>
            </div>

            {/* Botones */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                marginTop: 12,
              }}
            >
              <button
                type="button"
                className="button"
                onClick={() => setShowUpload(false)}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="button button--primary"
                disabled={uploading}
              >
                {uploading ? "Subiendo..." : "Subir factura"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
